import {ContentContainer} from '@coolcolduk/ui-mui';
import {ArrowBack, ArrowForward} from '@mui/icons-material';
import {Box, Button, CircularProgress, Stack, TextField} from '@mui/material';
import {useCallback, useState} from 'react';
import DropdownSeedQuestionStatus from '../../../atoms/dropdownSeedQuestionStatus/DropdownSeedQuestionStatus';
import PageHeader from '../../../components/PageHeader';
import GenerateSeedQuestionVariantPromptDialog from '../../../components/PromptDialogs/GenerateSeedQuestionVariantPromptDialog';
import {PrivateRouteProps} from '../../../config/routeType';
import {useCreateQuestionFromString} from '../../../hooks/question/useCreateQuestionFromString';
import LoadingPage from '../../other/LoadingPage';
import AdminSeedQuestionHeader from './AdminSeedQuestionHeader';
import AdminSeedQuestionNotFound from './AdminSeedQuestionNotFound';
import useAdminSeedQuestionPage from './useAdminSeedQuestionPage';

export default function AdminSeedQuestionPage(_props: PrivateRouteProps) {
  const {
    seedQuestion,
    moduleId,
    isLoading,
    modulesLoading,
    updateLoading,
    questionCount,
    questionCountLoading,
    navigationLoading,
    hasPrev,
    hasNext,
    formData,
    handleFormDataChange,
    handleBack,
    handleSave,
    handleFetchQuestionCount,
    handlePrev,
    handleNext,
  } = useAdminSeedQuestionPage();

  const [promptDialogOpen, setPromptDialogOpen] = useState(false);
  const {createQuestionFromString} = useCreateQuestionFromString();

  const handleCreateQuestionFromString = useCallback(
    (questionJsonString: string): Promise<void> => {
      if (!seedQuestion || !moduleId) return Promise.reject(new Error('Missing seed question or module'));
      return createQuestionFromString(
        moduleId,
        seedQuestion.topic,
        questionJsonString,
        undefined,
        undefined,
        seedQuestion.id,
      ).then(() => undefined);
    },
    [createQuestionFromString, moduleId, seedQuestion],
  );

  const handleOpenPromptDialog = useCallback(() => {
    setPromptDialogOpen(true);
  }, []);

  const handleClosePromptDialog = useCallback(() => {
    setPromptDialogOpen(false);
  }, []);

  if (isLoading || modulesLoading) {
    return (
      <LoadingPage
        btnText="Retry Loading"
        message="Taking longer than expected? Try reloading."
        showButtonAfterMs={5000}
        onClick={handleBack}
      />
    );
  }

  if (!seedQuestion || !moduleId) {
    return <AdminSeedQuestionNotFound onBack={handleBack} />;
  }

  return (
    <ContentContainer data-testid="admin-seed-question-page">
      <PageHeader title="Seed Question View" />

      <AdminSeedQuestionHeader
        onBack={handleBack}
        onSave={handleSave}
        onGeneratePrompt={handleOpenPromptDialog}
        updateLoading={updateLoading}
      />

      <Box sx={{mt: 3, mb: 2, display: 'flex', gap: 2, alignItems: 'center'}}>
        <Button
          variant="outlined"
          startIcon={navigationLoading ? undefined : <ArrowBack />}
          onClick={handlePrev}
          disabled={!hasPrev || navigationLoading}
          sx={{minWidth: 120}}
        >
          {navigationLoading ? <CircularProgress size={20} color="inherit" /> : 'Previous'}
        </Button>
        <Button
          variant="outlined"
          endIcon={navigationLoading ? undefined : <ArrowForward />}
          onClick={handleNext}
          disabled={!hasNext || navigationLoading}
          sx={{minWidth: 120}}
        >
          {navigationLoading ? <CircularProgress size={20} color="inherit" /> : 'Next'}
        </Button>
      </Box>

      <Box sx={{mt: 1, mb: 3}}>
        <Button
          variant="outlined"
          onClick={handleFetchQuestionCount}
          disabled={questionCountLoading}
          sx={{minWidth: 200}}
        >
          {questionCountLoading ? (
            <>
              <CircularProgress size={20} sx={{mr: 1}} />
              Loading...
            </>
          ) : questionCount !== null ? (
            `Questions using this seed: ${questionCount}`
          ) : (
            'Show Question Count'
          )}
        </Button>
      </Box>

      <Stack spacing={3} sx={{mt: 3}}>
        <TextField
          label="Question Text"
          value={formData.questionText}
          onChange={(e) => handleFormDataChange({questionText: e.target.value})}
          multiline
          rows={4}
          fullWidth
        />

        <TextField
          label="Topic"
          value={formData.topic}
          onChange={(e) => handleFormDataChange({topic: e.target.value})}
          fullWidth
        />

        <DropdownSeedQuestionStatus
          status={formData.status ?? undefined}
          onChange={(status) => handleFormDataChange({status})}
          fullWidth
        />

        <TextField
          label="Options (one per line)"
          value={formData.options.join('\n')}
          onChange={(e) =>
            handleFormDataChange({
              options: e.target.value.split('\n').filter((line) => line.trim()),
            })
          }
          multiline
          rows={6}
          fullWidth
          helperText="Enter each option on a separate line"
        />

        <TextField
          label="Correct Answer"
          value={formData.answer}
          onChange={(e) => handleFormDataChange({answer: e.target.value})}
          fullWidth
        />
      </Stack>

      <GenerateSeedQuestionVariantPromptDialog
        open={promptDialogOpen}
        seedQuestion={seedQuestion}
        moduleId={moduleId}
        onClose={handleClosePromptDialog}
        onCreateQuestionFromString={handleCreateQuestionFromString}
      />
    </ContentContainer>
  );
}
